// ═══════════════════════════════════════════════════════════
// «Коментарі» картки клієнта — журнал записів з автором і часом
// (api/customers/comments.js, рішення 2026-10-13).
//
// Коментар зберігається одразу, окремо від форми картки: кнопка «Зберегти»
// картки його не торкається, тож дописане іншою людиною чи інтеграцією,
// поки картка відкрита, не зникає. Автор виправляє й видаляє свій запис;
// з правом видаляти клієнтів можна видалити будь-який.
// ═══════════════════════════════════════════════════════════
import React, { useState, useEffect, useCallback } from 'react';
import { useT } from '../contexts/I18nContext';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { useAppData } from '../contexts/DataContext';
import { apiFetch } from '../services/api';

const fmtWhen = (ts) => (ts
  ? new Date(ts).toLocaleString('uk-UA', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  : '');

export default function CustomerComments({ customerId, legacyText }) {
  const { t } = useT();
  const toast = useToast();
  const { currentUser, hasPermission } = useAuth();
  const { subscribeSync } = useAppData();
  const canWrite = hasPermission('clients_edit');
  const canDeleteAny = hasPermission('clients_delete');

  const [comments, setComments] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editText, setEditText] = useState('');

  const load = useCallback(async () => {
    if (!customerId) return;
    try {
      const r = await apiFetch(`/api/customers/comments?customerId=${encodeURIComponent(customerId)}`);
      setComments(r.comments || []);
      setHasMore(!!r.hasMore);
    } catch { /* лишаємо, що було */ }
    setLoading(false);
  }, [customerId]);

  useEffect(() => { setLoading(true); load(); }, [load]);
  // Хтось інший (людина чи інтеграція) додав або змінив коментар.
  useEffect(() => (subscribeSync ? subscribeSync('customer_comments', load) : undefined), [subscribeSync, load]);

  const loadOlder = async () => {
    const oldest = comments[comments.length - 1]?.createdAt;
    if (!oldest) return;
    try {
      const r = await apiFetch(`/api/customers/comments?customerId=${encodeURIComponent(customerId)}&before=${oldest}`);
      setComments(prev => [...prev, ...(r.comments || []).filter(c => !prev.some(p => p.id === c.id))]);
      setHasMore(!!r.hasMore);
    } catch { /* ignore */ }
  };

  const add = async () => {
    const text = draft.trim();
    if (!text || busy) return;
    setBusy(true);
    try {
      const r = await apiFetch('/api/customers/comments', { method: 'POST', body: { customerId, text } });
      setComments(prev => [r.comment, ...prev.filter(c => c.id !== r.comment.id)]);
      setDraft('');
    } catch (err) {
      toast.error(err?.message || t('custComments.addFailed', 'Не вдалося додати коментар'));
    }
    setBusy(false);
  };

  const saveEdit = async () => {
    const text = editText.trim();
    if (!text || busy) return;
    setBusy(true);
    try {
      const r = await apiFetch('/api/customers/comments', { method: 'PATCH', body: { id: editId, text } });
      setComments(prev => prev.map(c => (c.id === editId ? r.comment : c)));
      setEditId(null);
    } catch (err) {
      toast.error(err?.message || t('custComments.saveFailed', 'Не вдалося зберегти'));
    }
    setBusy(false);
  };

  const remove = async (c) => {
    if (busy || !window.confirm(t('custComments.confirmDelete', 'Видалити коментар?'))) return;
    setBusy(true);
    try {
      await apiFetch(`/api/customers/comments?id=${encodeURIComponent(c.id)}`, { method: 'DELETE' });
      setComments(prev => prev.filter(x => x.id !== c.id));
    } catch (err) {
      toast.error(err?.message || t('custComments.deleteFailed', 'Не вдалося видалити'));
    }
    setBusy(false);
  };

  if (!customerId) {
    return <p className="cust-comments-hint">{t('custComments.afterCreate', 'Коментарі можна додавати після створення картки.')}</p>;
  }

  const mine = (c) => c.author.kind === 'user' && c.author.uid === currentUser?.uid;

  return (
    <div className="cust-comments">
      {canWrite && (
        <div className="cust-comments-new">
          <textarea
            rows="2"
            value={draft}
            placeholder={t('custComments.placeholder', 'Новий коментар…')}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); add(); } }}
          />
          <button type="button" className="btn btn-primary btn-sm" disabled={busy || !draft.trim()} onClick={add}>
            {t('custComments.add', 'Додати')}
          </button>
        </div>
      )}

      {loading ? (
        <p className="cust-comments-hint">{t('common.loading', 'Завантаження…')}</p>
      ) : comments.length === 0 && !legacyText ? (
        <p className="cust-comments-hint">{t('custComments.empty', 'Коментарів ще немає.')}</p>
      ) : (
        <ul className="cust-comments-list">
          {comments.map(c => (
            <li key={c.id} className={`cust-comment${c.author.kind === 'integration' ? ' integration' : ''}`}>
              <div className="cust-comment-head">
                <span className="cust-comment-author">
                  {c.author.kind === 'integration' ? '🔌 ' : ''}{c.author.name || t('custComments.unknown', 'Невідомий')}
                </span>
                <span className="cust-comment-when">
                  {fmtWhen(c.createdAt)}{c.editedAt ? ` · ${t('custComments.edited', 'виправлено')}` : ''}
                </span>
                {canWrite && (mine(c) || canDeleteAny) && editId !== c.id && (
                  <span className="cust-comment-actions">
                    {mine(c) && (
                      <button type="button" className="btn-icon" title={t('custComments.edit', 'Виправити')}
                        onClick={() => { setEditId(c.id); setEditText(c.text); }}>✏️</button>
                    )}
                    <button type="button" className="btn-icon" title={t('custComments.delete', 'Видалити')}
                      onClick={() => remove(c)}>🗑️</button>
                  </span>
                )}
              </div>
              {editId === c.id ? (
                <div className="cust-comments-new">
                  <textarea rows="2" value={editText} onChange={e => setEditText(e.target.value)} />
                  <button type="button" className="btn btn-primary btn-sm" disabled={busy || !editText.trim()} onClick={saveEdit}>
                    {t('custComments.save', 'Зберегти')}
                  </button>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => setEditId(null)}>
                    {t('common.cancel', 'Скасувати')}
                  </button>
                </div>
              ) : (
                <div className="cust-comment-text">{c.text}</div>
              )}
            </li>
          ))}
          {/* «Коментарі» до 13.10 були одним текстом у картці — показуємо як найстаріший запис. */}
          {legacyText && !hasMore && (
            <li className="cust-comment legacy">
              <div className="cust-comment-head">
                <span className="cust-comment-author">{t('custComments.legacy', 'Попередні коментарі')}</span>
              </div>
              <div className="cust-comment-text">{legacyText}</div>
            </li>
          )}
        </ul>
      )}
      {hasMore && (
        <button type="button" className="btn btn-secondary btn-sm" onClick={loadOlder}>
          {t('custComments.older', 'Показати раніші')}
        </button>
      )}
    </div>
  );
}
